import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useConfirm } from "./ConfirmModal";
import Icons from "../icons";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";

/* Verification status → badge styling. */
const VERIFY = {
  passed: { label: "Passed", cls: "bg-emerald-50 text-emerald-700 ring-emerald-600/20" },
  failed: { label: "Failed", cls: "bg-rose-50 text-rose-700 ring-rose-600/20" },
  partial: { label: "Partial", cls: "bg-amber-50 text-amber-700 ring-amber-600/20" },
  not_run: { label: "Not run", cls: "bg-slate-100 text-slate-600 ring-slate-500/20" },
};

function VerifyBadge({ status }) {
  const v = VERIFY[status] || VERIFY.not_run;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${v.cls}`}>
      {v.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  New requirement modal                                              */
/* ------------------------------------------------------------------ */
function NewRequirementModal({ boards, userkey, onClose, onCreated }) {
  const [reqId, setReqId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("M");
  const [boardId, setBoardId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const canSave = reqId.trim() && title.trim();

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setErr("");
    try {
      const res = await fetch(`${API_BASE_URL}requirements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          req_id: reqId.trim(),
          title: title.trim(),
          description: description.trim(),
          priority,
          board_id: boardId || null,
          userkey: userkey || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      onCreated();
    } catch (e) {
      setErr(e.message || "Could not create requirement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 flex w-full max-w-lg flex-col overflow-hidden ${DIALOG_HEIGHT} rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-center justify-between border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold text-slate-900">New requirement</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Req ID</label>
              <input value={reqId} onChange={(e) => setReqId(e.target.value)} placeholder="FR-RPT-01" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short requirement title" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Description <span className="text-slate-400">(optional)</span>
            </label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls}>
                <option value="M">Mandatory (M)</option>
                <option value="S">Should (S)</option>
                <option value="C">Could (C)</option>
                <option value="W">Won't (W)</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Test Suite</label>
              <select value={boardId} onChange={(e) => setBoardId(e.target.value)} className={inputCls}>
                <option value="">— none —</option>
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>
          {err && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
        </div>
        <div className="flex flex-none justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSave || saving}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Link test cases modal                                              */
/* ------------------------------------------------------------------ */
function LinkModal({ requirement, boards, onClose, onSaved }) {
  const [boardId, setBoardId] = useState(requirement.board_id || (boards[0]?.id ?? ""));
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(
    () => new Set((requirement.tests || []).map((t) => t.testcasekey))
  );
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!boardId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}testcases?board_id=${encodeURIComponent(boardId)}`);
        const body = await res.json().catch(() => ({}));
        if (!cancelled) setCases(Array.isArray(body?.data) ? body.data : []);
      } catch {
        if (!cancelled) setCases([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? cases.filter((c) => (c.name || "").toLowerCase().includes(q)) : cases;
  }, [cases, query]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}requirements/${encodeURIComponent(requirement.requirementkey)}/links`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ testcase_keys: Array.from(selected) }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (e) {
      alert(`Could not save links: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative z-10 flex ${DIALOG_HEIGHT} w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-card shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="font-semibold text-slate-900">Link test cases</h3>
            <p className="text-xs text-slate-500">{requirement.req_id} · {requirement.title}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-none space-y-3 px-5 py-4">
          <select value={boardId} onChange={(e) => setBoardId(e.target.value)} className={inputCls}>
            <option value="">Select a test suite…</option>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <div className="relative">
            <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search test cases…"
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
            />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto border-t border-slate-100 px-5 py-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-slate-400">
              <Icons.Spinner className="h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">No test cases.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((c) => (
                <li key={c.testcasekey}>
                  <label className="flex cursor-pointer items-center gap-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(c.testcasekey)}
                      onChange={() => toggle(c.testcasekey)}
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">{c.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-none items-center justify-between border-t border-slate-100 px-5 py-4">
          <span className="text-sm text-slate-500">{selected.size} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save links"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Traceability view                                                  */
/* ================================================================== */
export default function TraceabilityView({ boards = [], userkey }) {
  const [ask, confirmDialog] = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [linkFor, setLinkFor] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}requirements/traceability`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setRows(Array.isArray(body?.data) ? body.data : []);
    } catch (e) {
      setError(e.message || "Failed to load traceability.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const remove = async (row) => {
    if (!(await ask({ title: "Delete requirement", message: `Delete requirement "${row.req_id}"?` }))) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}requirements/${encodeURIComponent(row.requirementkey)}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      alert(`Could not delete: ${e.message}`);
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Trace requirements to their test cases and latest verification result.
        </p>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
        >
          <Icons.Plus className="h-4 w-4" />
          New requirement
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
          <Icons.Spinner className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-card px-6 py-16 text-center">
          <h3 className="font-semibold text-slate-800">No requirements yet</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
            Add requirements and link them to test cases to build a traceability matrix.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Req ID</th>
                  <th className="px-4 py-3">Requirement</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Tests</th>
                  <th className="px-4 py-3">Coverage</th>
                  <th className="px-4 py-3">Verification</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isOpen = expanded.has(row.requirementkey);
                  return (
                    <React.Fragment key={row.requirementkey}>
                      <tr className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">{row.req_id}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => toggleExpand(row.requirementkey)}
                            className="flex items-center gap-1.5 text-left text-sm font-medium text-slate-800 hover:text-brand-700"
                          >
                            <Icons.ChevronRight className={`h-4 w-4 flex-none text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                            {row.title}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{row.priority}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {row.total}
                          {row.total > 0 && (
                            <span className="ml-1 text-xs text-slate-400">
                              ({row.passed}✓ {row.failed}✗ {row.pending}·)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row.coverage_status === "covered" ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                              <Icons.Check className="h-3.5 w-3.5" /> Covered
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-600">
                              <Icons.X className="h-3.5 w-3.5" /> Not covered
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3"><VerifyBadge status={row.verification_status} /></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => setLinkFor(row)}
                              className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            >
                              Links
                            </button>
                            <button
                              type="button"
                              onClick={() => remove(row)}
                              aria-label="Delete requirement"
                              className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"
                            >
                              <Icons.Trash className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-slate-100 bg-slate-50/60">
                          <td colSpan={7} className="px-4 py-3">
                            {row.tests.length === 0 ? (
                              <p className="text-sm text-slate-400">No test cases linked.</p>
                            ) : (
                              <ul className="space-y-1">
                                {row.tests.map((t) => (
                                  <li key={t.testcasekey} className="flex items-center gap-2 text-sm">
                                    <span
                                      className={[
                                        "h-1.5 w-1.5 rounded-full",
                                        t.status === "passed" ? "bg-emerald-500" : t.status === "failed" ? "bg-rose-500" : "bg-slate-300",
                                      ].join(" ")}
                                    />
                                    <span className="text-slate-700">{t.name}</span>
                                    <span className="text-xs text-slate-400">({t.status})</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showNew && (
        <NewRequirementModal
          boards={boards}
          userkey={userkey}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      )}
      {linkFor && (
        <LinkModal
          requirement={linkFor}
          boards={boards}
          onClose={() => setLinkFor(null)}
          onSaved={() => {
            setLinkFor(null);
            load();
          }}
        />
      )}
      {/* The questions handlers ask before they act (useConfirm). */}
      {confirmDialog}
    </div>
  );
}
